<template>
  <div class="add_new_inner wrap-surg-add">
    <div class="subtitle center no_mobile">הזן פרטי ניתוח</div>
    <h2 class="subtitle_inner mobile_only">הזן פרטי ניתוח:</h2>

    <ValidationObserver ref="surgeryDetailsForm">
      <form @submit.prevent="onSubmit" name="surgeryDetailsForm">
        <SelectedSurgery ref="selectedSurgery" @resetForm="resetForm" />
        <InsuranceDetails ref="insuranceDetails" />
        <InsuranceSurgeryDetails ref="insuranceSurgeryDetails" />
        <AnesthesiaIndication ref="anesthesiaIndication" />
        <TreatmentHistory ref="treatmentHistory" />
        <CurrentDiagnosis ref="currentDiagnosis" />
        <PastDiagnosis ref="pastDiagnosis" />
        <PhysicalCheck ref="physicalCheck" />
        <MedicationSensitivity ref="medicationSensitivity" />
        <MaterialSensitivity ref="materialSensitivity" />

        <div class="surg-bottom-actions center">
          <button type="submit" class="bt_blue">
            הבא
            <vue-loaders-ball-clip-rotate
              v-show="isLoading"
              color="white"
              scale="1.2"
              style="position: relative;top: 9px;"
            ></vue-loaders-ball-clip-rotate>
          </button>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from "vuex";
import SelectedSurgery from "./SelectedSurgery.vue";
import InsuranceDetails from "./InsuranceDetails.vue";
import InsuranceSurgeryDetails from "./InsuranceSurgeryDetails.vue";
import TreatmentHistory from "./TreatmentHistory.vue";
import CurrentDiagnosis from "./CurrentDiagnosis.vue";
import PastDiagnosis from "./PastDiagnosis.vue";
import PhysicalCheck from "./PhysicalCheck.vue";
import MedicationSensitivity from "./MedicationSensitivity.vue";
import MaterialSensitivity from "./MaterialSensitivity.vue";
import AnesthesiaIndication from "./AnesthesiaIndication.vue";
import SurgSidesErr from "@/components/Popups/SurgSidesErr.vue";
import NewFeature from "@/components/Popups/NewFeature.vue";

// Constants
const HEADER_OFFSET = 100;
const MOBILE_BREAKPOINT = 1200;

// Per client request, we surface a warning when no insurance agreement
// exists for the selected surgery code, but do NOT block submission.
// Flip to true to make the missing-agreement warning blocking again.
const BLOCK_SUBMIT_ON_MISSING_INSURANCE_AGREEMENT = false;

/**
 * SurgeryDetails Step Component
 * Container for surgery selection and insurance details forms.
 * State is managed in Vuex store (Surgery module)
 */
export default {
  name: "SurgeryDetails",
  components: {
    SelectedSurgery,
    InsuranceDetails,
    InsuranceSurgeryDetails,
    AnesthesiaIndication,
    TreatmentHistory,
    CurrentDiagnosis,
    PastDiagnosis,
    PhysicalCheck,
    MedicationSensitivity,
    MaterialSensitivity
  },
  data() {
    return {
      isLoading: false,
      isMobile: window.innerWidth < MOBILE_BREAKPOINT
    };
  },
  watch: {
    isAnesthesiaIndicationRequired(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$modal.show(NewFeature, {
          title: "רופא/ה יקר/ה,",
          content:
            "קבלת התחייבות עבור בדיקה תחת הרדמה תתאפשר בכפוף לאחת מהאינדיקציות שאושרו ע״י הקופה בלבד.",
          buttonText: "הבנתי"
        });
      }
    }
  },
  computed: {
    ...mapState("Surgery", {
      surgery: state => state.surgery,
      isInsuranceType: state => state.isInsuranceType,
      insuranceSurgeryTemp: state => state.insuranceSurgeryTemp,
      insuranceForm: state => state.insuranceForm,
      isEditMode: state => state.isEditMode
    }),
    ...mapGetters("Surgery", [
      "isPayTypeCareService",
      "isAnesthesiaIndicationRequired"
    ]),

    /**
     * Checks if patient is marked as "healthy" (no background diagnoses needed)
     */
    isHealthy() {
      return this.surgery.background_diagnosis === "healthy";
    },

    /**
     * Gets the surgery ID from route or session storage
     */
    surgeryId() {
      return this.isEditMode
        ? this.$route.params.id
        : sessionStorage.getItem("temp_surgery");
    }
  },
  methods: {
    ...mapMutations("Surgery", [
      "SET_SURGERY",
      "SET_INSURANCE_SURGERY_TEMP",
      "SET_CURRENT_STEP"
    ]),
    ...mapActions("Surgery", [
      "setSurgeryOrders",
      "editSurgeryOrders",
      "setInitialSurgeryOrders"
    ]),

    /**
     * Handles form submission - validates all fields and business rules
     * Then calls API to save orders and navigates to next step
     */
    async onSubmit() {
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        // Step 1: Validate form fields
        const validationResult = await this.validate();
        if (!validationResult.valid) {
          if (validationResult.firstErrorRef) {
            this.scrollToError(validationResult.firstErrorRef);
          }
          return;
        }

        // Step 2: Check insurance surgery codes are properly selected
        if (this.checkSelectedInsurance()) {
          return;
        }

        // Step 3: Check surgery sides are properly set
        if (!this.checkSurgerySides()) {
          this.$modal.show(SurgSidesErr);
          return;
        }

        // Step 3.5: Validate anesthesia indication if needed
        if (this.isAnesthesiaIndicationRequired) {
          const anesthesiaValidation = this.$refs.anesthesiaIndication?.validate();
          if (anesthesiaValidation && !anesthesiaValidation.valid) {
            this.scrollToError("anesthesiaIndication");
            return;
          }
        }

        // Step 3.75: Prompt for related sub-procedure codes when applicable
        // (e.g. codes 701/703/702.2-4/750 => offer 734/702.5). This never
        // blocks navigation - it only adds accepted codes before saving.
        await this.$refs.selectedSurgery?.maybeShowAdditionalPlannedCodesPrompt();

        // Step 3.9: Codes added by that prompt are appended to
        // insuranceSurgeryTemp after step 1 already validated the insurer code
        // fields. When such a code has more than one insurer code, it cannot be
        // resolved automatically and the doctor has to pick one - so check
        // those rows here instead of saving an order with an empty insurer code.
        if (await this.hasUnselectedInsuranceSurgeryCode()) {
          this.scrollToError("insuranceSurgeryDetails");
          return;
        }

        // Step 4: Ensure at least one surgery is marked as primary
        this.ensurePrimarySurgery();

        // Step 5: Set insurance details on surgery data
        this.setSurgeryInsuranceDetails();

        // Step 6: Call API to save surgery orders (also fetches implants)
        const success = await this.saveSurgeryOrders();
        if (!success) return;

        // Step 7: Navigate to next step (implants are already loaded by store action)
        this.SET_CURRENT_STEP(4);

        // Step 8: Show tutorial for implants step
        this.showImplantsTutorial();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Lazy-loads the intro utility and shows the tutorial bound to a
     * specific element class with the given intro text.
     */
    showTutorial(elClass, intro) {
      import("@/utilities/intro.js").then(({ setTutorial }) => {
        setTutorial.call(this, elClass, intro);
      });
    },

    showImplantsTutorial() {
      this.showTutorial(
        "intro-implants",
        `רופא/ה יקר/ה, בכדי שנוכל להיערך בצורה מיטבית לניתוח, ובהתאם לקוד שהוזן, נבקשך לעדכן בשדה הייעודי החדש איזו חברה תספק משתלים ייעודים לניתוח. לתשומת ליבך, הוספת המסמכים וסיכומים לקראת הניתוח הועברה לשלב הבא. תודה`
      );
    },

    /**
     * Saves surgery orders via API (create or edit)
     * @returns {Promise<boolean>} Success status
     */
    async saveSurgeryOrders() {
      const surgeryId = this.surgeryId;
      if (!surgeryId) {
        console.error("No surgery ID found");
        return false;
      }

      let success = true;
      if (this.isEditMode || this.surgery.initial_selected_surgery.length) {
        success = await this.editSurgeryOrders({ surgeryId });
      } else {
        success = await this.setSurgeryOrders({ surgeryId });
      }

      return success;
    },

    /**
     * Whether any insurance surgery row offers insurer codes but none was
     * selected - the state a row lands in when its insurer code could not be
     * resolved automatically (more than one option).
     * Re-displays the form's validation messages so the empty field is marked.
     * @returns {Promise<boolean>} true when the doctor still has to pick a code
     */
    async hasUnselectedInsuranceSurgeryCode() {
      if (!this.isInsuranceType) return false;

      // Rows added by the prompt render on the next tick
      await this.$nextTick();

      const missing = this.insuranceSurgeryTemp?.some(
        item => item && item.insurance_surgery_codes?.length && !item.code
      );
      if (!missing) return false;

      await this.$refs.surgeryDetailsForm?.validate();
      return true;
    },

    checkSelectedInsurance() {
      const isInsurance =
        this.isInsuranceType &&
        (this.isPayTypeCareService ||
          this.surgery.isInsuranceCompany === "false") &&
        this.insuranceSurgeryTemp?.length;

      if (!isInsurance) return false;

      const missingAgreement = this.insuranceSurgeryTemp.find(
        s =>
          s &&
          !s.insurance_surgery_codes?.length &&
          s.code_name_hospital !== "1 > RHINOPLASTY"
      );
      if (!missingAgreement) return false;

      // !!Removed for now by client request
      // this.$modal.hideAll();
      // this.$modal.show(NewFeature, {
      //   title: "רופא/ה יקר/ה",
      //   content:
      //     "לתשומת ליבך, לקוד הניתוח הנבחר לא קיים הסכם מול הקופה. לקבלת עזרה, יש ליצור קשר עם מוקד הרופאים בטלפון :099594809",
      //   buttonText: "הבנתי"
      // });

      return BLOCK_SUBMIT_ON_MISSING_INSURANCE_AGREEMENT;
    },

    /**
     * Checks if surgery sides are properly set when same surgery is added multiple times
     * @returns {boolean} true if valid, false if sides are missing
     */
    checkSurgerySides() {
      if (
        !this.surgery.selected_surgery ||
        !this.surgery.selected_surgery.length
      ) {
        return true;
      }

      // Group surgeries by hospital code
      let groupedByCodeHospital = this.surgery.selected_surgery.reduce(
        (acc, item) => {
          const key = item.code_name_hospital;
          acc[key] = acc[key] || [];
          acc[key].push(item);
          return acc;
        },
        {}
      );

      // Only keep groups with more than one item (duplicates)
      groupedByCodeHospital = Object.fromEntries(
        Object.entries(groupedByCodeHospital).filter(
          ([, value]) => value.length > 1
        )
      );

      // Check that all duplicate groups have sides specified
      return Object.values(groupedByCodeHospital).every(
        items => !items.some(item => item.side === "")
      );
    },

    /**
     * Merges insurance details into surgery temp data
     */
    setSurgeryInsuranceDetails() {
      if (!this.insuranceSurgeryTemp?.length) return;

      const updated = this.insuranceSurgeryTemp.map((row, index) => {
        const selectedSurgery = this.surgery.selected_surgery[index];
        if (!selectedSurgery) return row;

        return {
          ...selectedSurgery,
          ...row
        };
      });

      this.SET_INSURANCE_SURGERY_TEMP(updated);
    },

    /**
     * Resets form validation state
     */
    resetForm() {
      this.$refs.surgeryDetailsForm?.reset();
    },

    /**
     * Auto-adds partial form data before validation
     * This ensures data being edited is not lost when navigating
     */
    async addBlockDefaults() {
      // Add selected surgery if form has data
      if (this.$refs.selectedSurgery?.surg_info?.code_name_hospital) {
        await this.$refs.selectedSurgery.addBlock();
      }
      // Add current diagnosis if form has data
      if (this.$refs.currentDiagnosis?.diagnosis_info?.name) {
        await this.$refs.currentDiagnosis.addBlock();
      }
      // Add past diagnosis if form has data
      if (this.$refs.pastDiagnosis?.past_diagnosis?.diagnosis_name) {
        await this.$refs.pastDiagnosis.addBlock();
      }
      // Add medication sensitivity if any field has data
      if (this.hasPartialFormData(this.$refs.medicationSensitivity?.formData)) {
        await this.$refs.medicationSensitivity.addBlock();
      }
      // Add material sensitivity if any field has data
      if (this.hasPartialFormData(this.$refs.materialSensitivity?.formData)) {
        await this.$refs.materialSensitivity.addBlock();
      }
    },

    /**
     * Validates the form and returns validation result with error details
     * Called by parent component during navigation
     * @returns {Object} { valid: boolean, errors: Object, firstErrorRef: string }
     */
    async validate() {
      // First, auto-add any partial form data
      await this.addBlockDefaults();

      // Validate the form
      const isFormValid = await this.$refs.surgeryDetailsForm?.validate();
      const errors = this.$refs.surgeryDetailsForm?.errors || {};

      let firstErrorRef = null;
      const validationErrors = {};

      // Check for selected surgery
      const noSurgery = !this.surgery.selected_surgery?.length;
      this.$refs.selectedSurgery?.setError(noSurgery);
      if (noSurgery) {
        validationErrors.surgery = true;
        firstErrorRef = firstErrorRef || "selectedSurgery";
      }

      // Check insurance-related errors
      if (
        errors.insurance_type?.length ||
        errors.isInsuranceCompany?.length ||
        errors.insuranceCompany?.length ||
        errors.payType?.length ||
        errors.insuranceClaim?.length ||
        errors.isPayRequired?.length
      ) {
        validationErrors.insurance = true;
        firstErrorRef = firstErrorRef || "insuranceDetails";
      }

      // Check insurance surgery code errors
      const insuranceErrors = Object.entries(errors)
        .filter(([key]) => key.includes("insuranceSurgeryCode"))
        .map(([, value]) => value);
      const hasInsuranceErr = insuranceErrors.some(err => err?.length);

      if (
        (this.isInsuranceType && hasInsuranceErr) ||
        errors.specialInsuranceCode?.length
      ) {
        validationErrors.insuranceSurgery = true;
        firstErrorRef = firstErrorRef || "insuranceSurgeryDetails";
      }

      // Check past diagnosis (required unless healthy)
      const noPastDiagnosis =
        !this.isHealthy && !this.surgery.past_diagnosis?.length;
      this.$refs.pastDiagnosis?.setError(noPastDiagnosis);
      if (noPastDiagnosis) {
        validationErrors.pastDiagnosis = true;
        firstErrorRef = firstErrorRef || "pastDiagnosis";
      }

      this.$refs.currentDiagnosis?.setError(false);

      const hasMedPartialData = this.hasPartialFormData(
        this.$refs.medicationSensitivity?.formData
      );
      this.$refs.medicationSensitivity?.setError(hasMedPartialData);
      if (hasMedPartialData) {
        validationErrors.medicationSensitivity = true;
        firstErrorRef = firstErrorRef || "medicationSensitivity";
      }

      const hasMatPartialData = this.hasPartialFormData(
        this.$refs.materialSensitivity?.formData
      );
      this.$refs.materialSensitivity?.setError(hasMatPartialData);
      if (hasMatPartialData) {
        validationErrors.materialSensitivity = true;
        firstErrorRef = firstErrorRef || "materialSensitivity";
      }

      // Check physical check (required)
      if (errors.phisycal_check?.length) {
        validationErrors.physicalCheck = true;
        firstErrorRef = firstErrorRef || "physicalCheck";
      }

      const isValid = isFormValid && Object.keys(validationErrors).length === 0;

      return {
        valid: isValid,
        errors: validationErrors,
        firstErrorRef,
        formErrors: errors
      };
    },

    /**
     * Scrolls to the first error element
     * @param {string} refName - The ref name of the component with error
     */
    scrollToError(refName) {
      const element = this.$refs[refName]?.$el || this.$refs[refName];
      if (element?.offsetTop) {
        let top = element.offsetTop;
        if (this.isMobile) top -= HEADER_OFFSET;

        setTimeout(() => {
          window.scrollTo({
            top,
            left: 0,
            behavior: "smooth"
          });
        }, 100);
      }
    },

    /**
     * Checks if a form object has any non-empty field (excluding notes)
     */
    hasPartialFormData(formData) {
      if (!formData) return false;
      const { notes, ...requiredFields } = formData;
      return Object.values(requiredFields).some(
        val => val !== "" && val != null
      );
    },

    /**
     * Ensures the first surgery is marked as primary if none is marked
     */
    ensurePrimarySurgery() {
      if (
        this.surgery.selected_surgery?.length &&
        !this.surgery.selected_surgery.find(s => s.is_primary)
      ) {
        const updated = [...this.surgery.selected_surgery];
        updated[0].is_primary = "primary";
        this.SET_SURGERY({ selected_surgery: updated });
      }
    }
  }
};
</script>
